import React, { useState, useEffect } from 'react';
import { getCalAvatar } from '../config/calAvatars';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  TrendingDown,
  TrendingUp,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { deriveAlerts, mapServerNotifications, loadReadIds, saveReadIds, CalAlert } from '../utils/calAlerts';

interface CalNotificationsProps {
  onMeterSelect?: (meterId: string) => void;
}

const CalNotifications: React.FC<CalNotificationsProps> = ({ onMeterSelect }) => {
  const theme = useTheme();
  const { getIdTokenClaims, user } = useAuth0();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [alerts, setAlerts] = useState<CalAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

  // Build alerts from the same per-meter dashboard data the fleet view uses (the
  // registry is the single source of truth), so notifications match the dashboard:
  // red -> critical, yellow -> warning, with the actual health reason + factor/limits.
  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const idToken = (await getIdTokenClaims())?.__raw;
      const headers = { Authorization: `Bearer ${idToken}` };
      const [dashResp, notifResp] = await Promise.all([
        fetch(`${API_ENDPOINT}/meters/dashboard`, { headers }),
        fetch(`${API_ENDPOINT}/notifications`, { headers }),
      ]);
      if (!dashResp.ok) throw new Error('Failed to fetch site data');

      const meters: any[] = (await dashResp.json()).meter_products || [];
      const derived = deriveAlerts(meters); // live health + drift
      let server: CalAlert[] = [];
      if (notifResp.ok) server = mapServerNotifications((await notifResp.json()).notifications || []);

      // The bell shows only UNREAD — anything marked read (here or in the
      // Notification Center) drops off. Read state = localStorage + server read_by.
      const readIds = loadReadIds(user?.email);
      const rank = (s: string) => (s === 'critical' ? 0 : s === 'warning' ? 1 : 2);
      const unread = [...derived, ...server]
        .filter((a) => !(readIds.has(a.id) || a.read))
        .sort((x, y) => rank(x.severity) - rank(y.severity));

      setAlerts(unread);
      setUnreadCount(unread.length);
    } catch (error) {
      console.error('Error fetching Cal alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark alerts read (localStorage + server for persisted ones). saveReadIds
  // broadcasts, so the bell + Notification Center both refresh.
  const markAlertsRead = async (list: CalAlert[]) => {
    if (!list.length) return;
    const readIds = loadReadIds(user?.email);
    list.forEach((a) => readIds.add(a.id));
    saveReadIds(user?.email, readIds);
    const idToken = (await getIdTokenClaims())?.__raw;
    const headers = { Authorization: `Bearer ${idToken}` };
    if (list.length > 3 && list.some((a) => a.server)) {
      fetch(`${API_ENDPOINT}/notifications/read-all`, { method: 'POST', headers }).catch(() => {});
    } else {
      list.filter((a) => a.server).forEach((a) =>
        fetch(`${API_ENDPOINT}/notifications/${encodeURIComponent(a.id)}/read`, { method: 'POST', headers }).catch(() => {}),
      );
    }
  };

  // Alerts only change when the underlying data changes (a new proving is ingested or
  // a verification/maintenance edit), so there's no point polling on a timer. Fetch
  // once on mount for the initial badge, then refresh on demand when the bell is opened.
  useEffect(() => {
    fetchAlerts();
    // Re-fetch when read state changes anywhere (e.g. marked read in the
    // Notification Center) so read items disappear from the bell immediately.
    const onReadChange = () => fetchAlerts();
    window.addEventListener('cal-notif-read-changed', onReadChange);
    return () => window.removeEventListener('cal-notif-read-changed', onReadChange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Refresh on open so the list reflects the latest health/drift.
    fetchAlerts();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAlertClick = (alert: CalAlert) => {
    markAlertsRead([alert]); // clicking dismisses it from the bell
    if (alert.meterId && onMeterSelect) {
      onMeterSelect(alert.meterId);
    }
    handleClose();
  };

  const handleClearAll = () => {
    markAlertsRead(alerts); // mark all read (syncs with the Notification Center)
    setAlerts([]);
    setUnreadCount(0);
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      case 'warning':
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case 'info':
        return <CheckCircleIcon sx={{ color: theme.palette.info.main }} />;
      default:
        return <NotificationsIcon />;
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="large"
        onClick={handleClick}
        color="inherit"
        sx={{
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              animation: unreadCount > 0 ? 'pulse 1.5s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.1)',
                  opacity: 0.9,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
              },
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disablePortal
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: 'calc(100vw - 48px)',
            maxHeight: 500,
            overflow: 'auto',
            mt: 1.5,
            borderRadius: 2,
            boxShadow: theme.shadows[4],
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Cal Alerts
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {alerts.length > 0 && (
                <Button
                  size="small"
                  onClick={handleClearAll}
                  sx={{ textTransform: 'none' }}
                >
                  Mark all read
                </Button>
              )}
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Real-time meter health monitoring by Cal
          </Typography>
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : alerts.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: theme.palette.success.main, mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              All meters operating normally
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cal is monitoring your site 24/7
            </Typography>
          </Box>
        ) : (
          <Box>
            {alerts.map((alert) => (
              <MenuItem
                key={alert.id}
                onClick={() => handleAlertClick(alert)}
                sx={{
                  py: 2,
                  px: 2,
                  borderLeft: `4px solid ${
                    alert.severity === 'critical'
                      ? theme.palette.error.main
                      : alert.severity === 'warning'
                      ? theme.palette.warning.main
                      : theme.palette.info.main
                  }`,
                  backgroundColor: !alert.read ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getSeverityIcon(alert.severity)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {alert.title}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {alert.message}
                      </Typography>
                      <Alert
                        severity={alert.severity === 'critical' ? 'error' : alert.severity}
                        sx={{
                          py: 0.5,
                          px: 1,
                          '& .MuiAlert-icon': { fontSize: 16 },
                          '& .MuiAlert-message': { fontSize: '0.75rem' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <img
                            src={getCalAvatar().path}
                            alt="Cal"
                            style={{
                              width: 14,
                              height: 14,
                              objectFit: 'cover',
                              borderRadius: '50%',
                              marginRight: 4,
                            }}
                          />
                          {alert.recommendation}
                        </Box>
                      </Alert>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {new Date(alert.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  }
                />
              </MenuItem>
            ))}
          </Box>
        )}

        {alerts.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Button
                size="small"
                onClick={fetchAlerts}
                sx={{ textTransform: 'none' }}
              >
                Refresh Alerts
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default CalNotifications;